import PropTypes from 'prop-types';
import { Toggle, IconButton } from '@nike/eds';
import { useState } from 'react';
import classnames from 'classnames';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as R from 'ramda';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import { useAtom } from 'jotai';
import { updateDictionaryTerm, delDictionaryTerm } from '../clientAPI';
import { DICTIONARY_TERM_TYPE } from '../constants';
import toUnicode from '../utils/stringToUicode';
import { isExistScope } from '../utils/authHelpers';
import {
  dictionaryUpdateMessageAtom,
  dictionaryUpdateHasErrorAtom,
} from '../atoms/dictionary';

function DictionaryTermCtr({
  term,
  id,
  dictionaryType,
  queryKey,
}: {
  term: DictionaryTerm;
  id: string;
  dictionaryType: string;
  queryKey: string;
}) {
  const [updateMsg, setUpdateMsg] = useAtom(dictionaryUpdateMessageAtom);
  const [updateHasError, setUpdateHasError] = useAtom(
    dictionaryUpdateHasErrorAtom,
  );
  const queryClient = useQueryClient();
  const loaderData = useLoaderData() as { access_token: string };
  const jwt = loaderData.access_token;
  const canWrite = isExistScope('search360:dictionary:write', jwt);
  const revalidator = useRevalidator();
  const statusMutation = useMutation({
    mutationFn: (data: Partial<DictionaryTerm>) => {
      return updateDictionaryTerm(data, dictionaryType);
    },
    onError: (err: keyable) => {
      console.log(err);
      setUpdateMsg(JSON.stringify(err));
      setUpdateHasError(true);
      err.status === 401 && revalidator.revalidate();
    },
    onSuccess: () => {
      setUpdateHasError(false);
      setUpdateMsg('Updated!');
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
  });
  const delMutation = useMutation({
    mutationFn: (id: string) => {
      return delDictionaryTerm(id, dictionaryType);
    },
    onMutate: () => {
      const indexKey = term['indexKey'];
      const prevDictionaryTerms = queryClient.getQueryData([
        queryKey,
      ]) as DictionaryResponse;
      const prevList = prevDictionaryTerms.terms[indexKey];
      const idx = R.findIndex((t: DictionaryTerm) => t.id === id, prevList);
      const updatedList = R.remove(idx, 1, prevList);

      const updatedDictionaryTerms = {
        terms: { ...prevDictionaryTerms.terms },
      };
      updatedDictionaryTerms.terms[indexKey] = updatedList;
      queryClient.setQueryData([queryKey], updatedDictionaryTerms);

      return { prevDictionaryTerms, updatedDictionaryTerms };
    },
    onError: (err: keyable, newTodo, context) => {
      console.log(err);
      setUpdateMsg(JSON.stringify(err));
      setUpdateHasError(true);
      queryClient.setQueryData([queryKey], context?.prevDictionaryTerms);
      err.status === 401 && revalidator.revalidate();
    },
    onSuccess: () => {
      setUpdateHasError(false);
      setUpdateMsg('Deleted!');
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
  });
  const inactive = term.status !== 'ACTIVE';
  const [disabled, setDisabled] = useState(inactive);
  const termCls = classnames('tw-leading-7 group-hover:tw-text-xl', {
    'tw-line-through tw-decoration-slate-400 tw-text-slate-400': disabled,
    'tw-text-indigo-700 tw-font-bold':
      term.source === DICTIONARY_TERM_TYPE.MANUAL,
    'group-hover:tw-font-bold': term.source === DICTIONARY_TERM_TYPE.IMPORTED,
  });
  const onToggleStatus = () => {
    statusMutation.mutate({
      id: term.id,
      status: disabled ? 'ACTIVE' : 'INACTIVE',
    });
    setDisabled(!disabled);
  };

  return (
    <div className='eds-type--body-2 hover:tw-cursor-pointer tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-group'>
      <div className={termCls}>{term.term}</div>
      {term.source === DICTIONARY_TERM_TYPE.IMPORTED && canWrite && (
        <div className='tw-hidden group-hover:tw-block'>
          <Toggle
            id={id}
            label='Disable'
            onChange={onToggleStatus}
            checked={disabled}
          />
        </div>
      )}
      {term.source === DICTIONARY_TERM_TYPE.MANUAL && canWrite && (
        <div className=''>
          <IconButton
            label='delete'
            icon='Delete'
            size='small'
            variant='ghost'
            onClick={() => {
              delMutation.mutate(String(term.id));
            }}
          />
        </div>
      )}
    </div>
  );
}

export default function DictionaryAlphabetCard({
  label,
  terms,
  paginationSize = 80,
  dictionaryType,
  queryKey,
}: {
  label: string;
  terms: DictionaryTerm[];
  paginationSize: number;
  dictionaryType: string;
  queryKey: string;
}) {
  const splitedTerms = R.splitEvery(paginationSize, terms);
  const [currentIdx, setCurrentIdx] = useState(0);
  const displayedData = splitedTerms[currentIdx] || [];
  const shouldPaginate = splitedTerms.length > 1;
  return (
    <div>
      <div className='tw-uppercase eds-type--subtitle-1 tw-text-left'>
        {label}
      </div>
      <div className='tw-text-left'>
        {displayedData.map((data, idx) => {
          const termCode = toUnicode(data.term);
          return (
            <DictionaryTermCtr
              key={`${termCode}_${idx}`}
              term={data}
              id={termCode}
              queryKey={queryKey}
              dictionaryType={dictionaryType}
            />
          );
        })}
      </div>
      {shouldPaginate && (
        <div className='tw-flex tw-items-center tw-justify-between tw-mt-6'>
          <IconButton
            icon='ArrowLeft'
            size='small'
            disabled={currentIdx === 0}
            onClick={() => {
              setCurrentIdx(currentIdx - 1);
            }}
            label='previous'
            variant='ghost'
          />
          <IconButton
            icon='ArrowRight'
            size='small'
            disabled={currentIdx === splitedTerms.length - 1}
            onClick={() => {
              setCurrentIdx(currentIdx + 1);
            }}
            label='next'
            variant='ghost'
          />
        </div>
      )}
    </div>
  );
}

DictionaryAlphabetCard.propTypes = {
  terms: PropTypes.arrayOf(
    PropTypes.shape({
      term: PropTypes.string,
    }),
  ),
  label: PropTypes.string,
  dictionaryType: PropTypes.string,
  queryKey: PropTypes.string,
};
